/**
 * OAuth callback handler for Aegix One.
 *
 * After a social provider (Google, Apple, Microsoft) completes
 * authentication, it redirects back here with auth params (code,
 * state, scope, ...). This page completes the flow by handing the
 * params dict to the lib's `allauthAdapter.loginWithOAuth`, which
 * posts them verbatim to `/_allauth/browser/v1/auth/provider/token`
 * and returns the user + JWT (Aegix's `JWTTokenStrategy`). The
 * provider then dispatches `login_success` and App.tsx re-renders
 * into the authenticated routes.
 *
 * ADR-118 #18: the redirect-callback flow is the lib's
 * `loginWithOAuth`. The adapter handles the JWT extraction (via
 * `meta.access_token`) uniformly with the other completion paths.
 */

import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Center, Spinner, Text, VStack } from '@chakra-ui/react';
import {
  useAuthActions,
  type AllauthUser,
} from '@aegix/auth-identity';
import { consumeReturnTo } from '@/lib/returnTo';

export default function AuthCallback() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const actions = useAuthActions<AllauthUser>();
  const navigate = useNavigate();
  const [error, setError] = useState('');

  useEffect(() => {
    const provider =
      searchParams.get('provider') ||
      localStorage.getItem('aegix_one_oauth_provider') ||
      '';
    const code = searchParams.get('code') || '';

    if (!code) {
      setError(t('authCallback.noCode'));
      return;
    }
    if (!actions.loginWithOAuth) {
      setError(t('authCallback.noOAuth'));
      return;
    }

    const params: Record<string, string> = {};
    searchParams.forEach((value, key) => {
      params[key] = value;
    });

    actions
      .loginWithOAuth({ provider, params })
      .then(() => {
        // The SSO transport resolves here rather than on the
        // authenticated /login route (the URL is /auth/callback when
        // auth flips) — land on the deep-link target preserved at the
        // login redirect, defaulting to home. `consumeReturnTo` only
        // ever returns safe same-app paths (open-redirect guard).
        navigate(consumeReturnTo() ?? '/', { replace: true });
      })
      .catch((err: unknown) => {
        setError(
          err instanceof Error
            ? err.message
            : t('social.failed'),
        );
      });
  }, [searchParams, actions, navigate, t]);

  if (error) {
    return (
      <Center h="100vh" bg="surface.primary">
        <VStack gap={4}>
          <Text color="red.500" fontSize="md">
            {error}
          </Text>
          <Text fontSize="sm" color="text.secondary">
            <a href="/login">{t('authCallback.returnToSignIn')}</a>
          </Text>
        </VStack>
      </Center>
    );
  }

  return (
    <Center h="100vh" bg="surface.primary">
      <VStack gap={4}>
        <Spinner size="lg" />
        <Text fontSize="sm" color="text.secondary">
          {t('authCallback.completing')}
        </Text>
      </VStack>
    </Center>
  );
}
